import { memo } from 'react';
import { cn } from '@/lib/utils';
import { OptimizedImage } from './OptimizedImage';
import { Trophy } from 'lucide-react';

interface LeagueBadgeProps {
  logoUrl: string | null | undefined;
  leagueName: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showFallbackIcon?: boolean;
}

// Mobile-optimized league badge sizes
const sizeMap = {
  sm: { width: 18, height: 18, class: 'w-[18px] h-[18px]', iconSize: 'h-2.5 w-2.5' },
  md: { width: 22, height: 22, class: 'w-[22px] h-[22px] sm:w-6 sm:h-6', iconSize: 'h-3 w-3 sm:h-3.5 sm:w-3.5' },
  lg: { width: 32, height: 32, class: 'w-8 h-8', iconSize: 'h-4 w-4' },
};

/**
 * Optimized league badge component with:
 * - Lazy loading
 * - Trophy icon fallback
 * - Proper sizing
 * - Memoization
 */
function LeagueBadgeComponent({
  logoUrl,
  leagueName,
  size = 'md',
  className,
  showFallbackIcon = true,
}: LeagueBadgeProps) {
  const { width, height, class: sizeClass, iconSize } = sizeMap[size];

  if (!logoUrl) {
    if (!showFallbackIcon) return null;
    return (
      <div 
        className={cn(
          sizeClass,
          'rounded bg-secondary/50 flex items-center justify-center shrink-0',
          className
        )}
        aria-hidden="true"
      >
        <Trophy className={cn(iconSize, 'text-muted-foreground')} />
      </div>
    );
  }

  return (
    <OptimizedImage
      src={logoUrl}
      alt=""
      width={width}
      height={height}
      className={cn(sizeClass, 'object-contain shrink-0', className)}
      fallback={
        showFallbackIcon ? (
          <div className={cn(sizeClass, 'rounded bg-secondary/50 flex items-center justify-center shrink-0', className)}>
            <Trophy className={cn(iconSize, 'text-muted-foreground')} />
          </div>
        ) : null
      }
    />
  );
}

export const LeagueBadge = memo(LeagueBadgeComponent, (prev, next) => {
  return (
    prev.logoUrl === next.logoUrl &&
    prev.leagueName === next.leagueName &&
    prev.size === next.size
  );
});
